import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown, Check } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { useStore } from "@/context/StoreContext";

const LINKS = [
  { href: "#store", label: "Store" },
  { href: "#features", label: "Fight Studio" },
  { href: "#karate-gis", label: "Karate GI’s" },
  { href: "#showcase", label: "Showcase" },
  { to: "/changelog", label: "Changelog" },
  { href: "#faq", label: "FAQ" },
];

const CurrencyPicker = ({ compact = false }) => {
  const { code, setCode, rates } = useStore();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (!e.target.closest?.("[data-currency-root]")) setOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  return (
    <div className="relative" data-currency-root>
      <button
        data-testid="currency-select"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 border border-white/12 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-white/70 transition-colors duration-300 hover:border-white/35 hover:text-white"
      >
        {code}
        <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.22 }}
            className={`glass absolute ${compact ? "left-0" : "right-0"} z-50 mt-2 w-52 border border-white/12 p-1`}
          >
            {Object.entries(rates).map(([c, meta]) => (
              <button
                key={c}
                data-testid={`currency-option-${c}`}
                onClick={() => {
                  setCode(c);
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between px-3 py-2.5 text-left transition-colors duration-200 hover:bg-white/6"
              >
                <span className="font-mono text-[11px] tracking-[0.14em] text-white/80">
                  {meta.symbol} {c}
                </span>
                <span className="flex items-center gap-2 text-[11px] text-white/35">
                  {meta.label}
                  {code === c && <Check className="h-3 w-3 text-[hsl(var(--orv-blue))]" />}
                </span>
              </button>
            ))}
            <p className="px-3 py-2 text-[10px] leading-relaxed text-white/25">
              Indicative conversion. Tebex bills in USD.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled ? "glass border-b border-white/8" : "border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-5 sm:px-8">
          <a href="#top" data-testid="nav-logo" className="group flex items-center gap-3.5">
            <img
              src={BRAND.mark}
              alt="Orvyn"
              className="h-8 w-auto shrink-0 transition-opacity duration-300 group-hover:opacity-80"
            />
            <span className="hidden h-8 w-px bg-white/12 sm:block" />
            <span className="hidden sm:block">
              <span className="block font-display text-[15px] font-bold uppercase tracking-[0.3em] leading-none text-white">
                Orvyn
              </span>
              <span className="mt-1.5 block font-mono text-[9px] uppercase tracking-[0.34em] text-white/35">
                FiveM Scripts
              </span>
            </span>
          </a>

          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) =>
              l.to ? (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 transition-colors duration-300 hover:text-white"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 transition-colors duration-300 hover:text-white"
                >
                  {l.label}
                </a>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <CurrencyPicker />
            <a
              href="#store"
              data-testid="nav-store-button"
              className="group relative hidden overflow-hidden bg-white px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-black sm:block"
            >
              <span className="relative z-10">Shop</span>
              <span className="absolute inset-0 z-0 -translate-y-full bg-[hsl(var(--orv-blue))] transition-transform duration-400 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-y-0" />
            </a>
            <button
              onClick={() => setOpen(true)}
              data-testid="nav-mobile-open"
              className="border border-white/12 p-2.5 text-white/70 lg:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-4 w-4" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#08080a] lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex h-[72px] items-center justify-between px-5">
              <span className="label">Menu</span>
              <button onClick={() => setOpen(false)} data-testid="nav-mobile-close" aria-label="Close menu">
                <X className="h-5 w-5 text-white/70" />
              </button>
            </div>
            <div className="px-5 pt-8">
              {LINKS.map((l, i) =>
                l.to ? (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 * i, duration: 0.5 }}
                  >
                    <Link
                      to={l.to}
                      onClick={() => setOpen(false)}
                      className="block border-b border-white/8 py-5 font-display text-2xl font-semibold uppercase tracking-tight text-white"
                    >
                      {l.label}
                    </Link>
                  </motion.div>
                ) : (
                  <motion.a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 * i, duration: 0.5 }}
                    className="block border-b border-white/8 py-5 font-display text-2xl font-semibold uppercase tracking-tight text-white"
                  >
                    {l.label}
                  </motion.a>
                )
              )}
              <a
                href="#store"
                onClick={() => setOpen(false)}
                className="mt-9 block w-full bg-white py-4 text-center font-mono text-xs uppercase tracking-[0.2em] text-black"
              >
                Shop the store
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
